// ---------------------------------------------------------------------------
// detourSpread — the pure heart of "detours along the ENTIRE route"
// (Morgan: "how do i know if detours are along the entire route and not
// clustered in the starting point").
//
// Extracted verbatim from DriveByOptions so the daily audit can attack it
// with synthetic worst cases (metro-dense start, quiet destination) —
// behavior is identical; only the home changed.
//
// Algorithm: bucket candidates by trip progress, then round-robin one per
// bucket per pass so start → middle → end all get seats, with a minimum
// geographic separation so picks don't blob, and a backfill so sparse
// buckets don't leave empty slots.
// ---------------------------------------------------------------------------
import type { LngLat } from "@/utils/polyline";

export interface SpreadCandidate<H extends { id: string; center: LngLat }> {
  h: H;
  score: number;
  /** 0..1 position along the trip. */
  progress: number;
}

function haversineM(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function spreadAlongTrip<H extends { id: string; center: LngLat }>(
  /** Score-sorted (best first), deduped, end-cutoff-filtered candidates. */
  unique: Array<SpreadCandidate<H>>,
  opts: { bucketCount: number; perBucket: number; max: number; minSepM: number },
): H[] {
  const { bucketCount, perBucket, max, minSepM } = opts;
  const buckets: Array<Array<SpreadCandidate<H>>> =
    Array.from({ length: bucketCount }, () => []);
  for (const s of unique) {
    const idx = Math.min(bucketCount - 1, Math.floor(s.progress * bucketCount));
    buckets[idx].push(s);
  }

  const out: H[] = [];
  const farEnoughFromPicked = (h: H): boolean => {
    for (const p of out) {
      if (haversineM(h.center, p.center) < minSepM) return false;
    }
    return true;
  };

  // Round-robin across buckets so stops are spread ALONG the route
  // (start → middle → end), not all clustered in the start city.
  for (let pass = 0; pass < perBucket; pass++) {
    for (const b of buckets) {
      if (out.length >= max) break;
      const cand = b[pass];
      if (cand && farEnoughFromPicked(cand.h)) out.push(cand.h);
    }
    if (out.length >= max) break;
  }

  // Backfill from any bucket if we still have headroom — better to show
  // extras than empty slots; min-sep still applies.
  if (out.length < max) {
    const taken = new Set(out.map((h) => h.id));
    for (const s of unique) {
      if (out.length >= max) break;
      if (taken.has(s.h.id)) continue;
      if (!farEnoughFromPicked(s.h)) continue;
      out.push(s.h);
      taken.add(s.h.id);
    }
  }
  return out.slice(0, max);
}
